/**
 * TanStack Router config — code-based routes (no codegen).
 *
 * Структура:
 *   rootRoute (просто Outlet)
 *     ├── /login                  (без guard — LoginPage)
 *     └── protectedLayout (guard) (AppShell + Outlet)
 *           ├── /                 → OverviewPage (Сводка)
 *           ├── /health           → HealthPage   (для отладки)
 *           ├── /managers, /callcenter, /violations, /reports, /calls, /settings
 *               → PlaceholderPage (Шаги 4-7)
 *
 * Гарда — beforeLoad на protectedLayout: если useAuth.user==null
 * (из QueryClient/Context — но мы используем context.queryClient или свой
 * глобальный AuthContext через React-tree, поэтому guard живёт прямо в
 * AuthGate-компоненте, а в роутере он только оборачивает Outlet).
 */
import {
  Outlet,
  Navigate,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { lazy, Suspense } from "react";

import { AppShell } from "@/components/AppShell";
import { useAuth, hasAccess } from "@/auth/AuthContext";
import { KbSplash } from "@/components/KbSplash";
// ── Ядро мессенджера — eager (стартовый путь /messenger + навигация внутри) ──
import { ChatPage } from "@/pages/ChatPage";
import { MessengerPage } from "@/pages/MessengerPage";
import { SharePage } from "@/pages/SharePage";
import { LoginPage } from "@/pages/LoginPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";

// ── Дашборд-страницы — LAZY. В messenger-бандл (VITE_APP_MODE=chat) не тянутся:
// на /messenger их не открывают; на дашбордах грузятся при переходе на маршрут.
// Имена экспортов — как в прежних статических импортах (менять нельзя: их же
// собирают sales/production/etc.). ──
const CallcenterPage = lazyRouteComponent(() => import("@/pages/CallcenterPage"), "CallcenterPage");
const CallsPage = lazyRouteComponent(() => import("@/pages/CallsPage"), "CallsPage");
const AdminAIDialogsPage = lazyRouteComponent(() => import("@/pages/AdminAIDialogsPage"), "AdminAIDialogsPage");
const HealthPage = lazyRouteComponent(() => import("@/pages/HealthPage"), "HealthPage");
const ManagersPage = lazyRouteComponent(() => import("@/pages/ManagersPage"), "ManagersPage");
const OverviewPage = lazyRouteComponent(() => import("@/pages/OverviewPage"), "OverviewPage");
const ProductionPage = lazyRouteComponent(() => import("@/pages/ProductionPage"), "ProductionPage");
const ProductionDetailPage = lazyRouteComponent(() => import("@/pages/ProductionDetailPage"), "ProductionDetailPage");
const SpecPage = lazyRouteComponent(() => import("@/pages/SpecPage"), "SpecPage");
const VisitsPage = lazyRouteComponent(() => import("@/pages/VisitsPage"), "VisitsPage");
const CalculatorPage = lazyRouteComponent(() => import("@/pages/CalculatorPage"), "CalculatorPage");
const CalculatorAdminPage = lazyRouteComponent(() => import("@/pages/CalculatorAdminPage"), "CalculatorAdminPage");
const ReportsPage = lazyRouteComponent(() => import("@/pages/ReportsPage"), "ReportsPage");
const ViolationsPage = lazyRouteComponent(() => import("@/pages/ViolationsPage"), "ViolationsPage");
// SettingsPage используется ВНУТРИ обёртки SettingsGate (не как route component) —
// ── «Страницы» — лёгкий редактор документов (BlockNote). Lazy: сам редактор ~1 МБ, в стартовый чанк не нужен.
const PagesPageLazy = lazy(() => import("@/pages/PagesPage").then(m => ({ default: m.PagesPage })));
const PageEditorPageLazy = lazy(() => import("@/pages/PageEditorPage").then(m => ({ default: m.PageEditorPage })));
const PageOpenPageLazy = lazy(() => import("@/pages/PageOpenPage").then(m => ({ default: m.PageOpenPage })));
// Проба SuperDoc: docx как есть (отдельный тяжёлый чанк, грузится только на этом маршруте).
const WordPageLazy = lazy(() => import("@/pages/WordPage").then(m => ({ default: m.WordPage })));
const SheetPageLazy = lazy(() => import("@/pages/SheetPage").then(m => ({ default: m.SheetPage })));


interface RouterContext {
  queryClient: QueryClient;
}


// ── Auth gate ──────────────────────────────────────────────────────────
function AuthGate() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <div className="muted">Проверка сессии…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // Sales dashboard — только сотрудники отдела продаж (или owner/admin/superuser).
  // На messenger-build (VITE_APP_MODE=chat) ограничение не применяем —
  // мессенджер доступен всем (CHATGATE для него отдельный).
  const IS_CHAT_APP_GATE = (import.meta.env.VITE_APP_MODE as string | undefined) === "chat";
  if (!IS_CHAT_APP_GATE && !hasAccess(user, "sales")) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Доступ ограничен</div>
          <div style={{ color: "var(--mute)", marginBottom: 16 }}>
            Дашборд отдела продаж доступен только сотрудникам отдела продаж.
            Обратитесь к администратору, если вам нужен доступ.
          </div>
          <a href="/messenger" className="btn btn-primary">Перейти в Мессенджер</a>
        </div>
      </div>
    );
  }
  return <AppShell />;
}


function GuestGate() {
  // Если уже залогинен — на /
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
}


const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: GuestGate,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: AuthGate,
});

// На messenger-build (VITE_APP_MODE=chat) корень "/" должен сразу вести в чат,
// не в OverviewPage. Иначе после login видно flash "Загрузка сводки".
const IS_CHAT_APP = (import.meta.env.VITE_APP_MODE as string | undefined) === "chat";
const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: IS_CHAT_APP ? () => <Navigate to="/messenger" replace /> : OverviewPage,
});

const healthRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/health",
  component: HealthPage,
});

const managersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/managers",
  component: ManagersPage,
});

const callcenterRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/callcenter",
  component: CallcenterPage,
});

const violationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/violations",
  component: ViolationsPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/reports",
  component: ReportsPage,
});

const callsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/calls",
  component: CallsPage,
});

function SettingsGate() {
  // Панель настроек переехала в CRM (владелец, 14.10.2026): любой старый адрес
  // (dashboard./settings, sales./settings) ведёт на кирпичные-беседки.рф/crm/settings.
  if (typeof window !== "undefined") {
    const apex = window.location.hostname.replace(/^(sales|dashboard|www)\./, "");
    window.location.replace(`https://${apex}/crm/settings`);
  }
  return <KbSplash/>;
}

const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings",
  component: SettingsGate,
});

const chatRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/chat",
  component: ChatPage,
});

function MessengerGate() {
  // Автономный гейт для /messenger — БЕЗ AppShell (без дашборд-sidebar).
  // /messenger — отдельная страница, не часть дашборда.
  const { user, isLoading } = useAuth();
  if (isLoading) return <KbSplash/>;
  if (!user) return <Navigate to="/login" replace />;
  return <MessengerPage />;
}

const messengerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/messenger",
  component: MessengerGate,
});

// Web Share Target API landing: PWA принимает шаринг из любого приложения
// (Telegram, Safari, Photos и т.п.) — Android вызывает POST /api/share/inbox,
// бэк редиректит сюда с text + att=ID,ID. Юзер выбирает чат — отправляем.
const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/messenger/share",
  component: SharePage,
});

// «Страницы» (/pages, /pages/$pageId) — без AppShell, как /messenger. Прототип 11.09.2026.
function PagesGate({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <KbSplash/>;
  if (!user) return <Navigate to="/login" replace />;
  return <Suspense fallback={<KbSplash/>}>{children}</Suspense>;
}
const pagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pages",
  component: () => <PagesGate><PagesPageLazy /></PagesGate>,
});
const pageEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pages/$pageId",
  component: () => <PagesGate><PageEditorPageLazy /></PagesGate>,
});
// Открыть docx/xlsx из чата/Диска как страницу (импорт идемпотентен).
const pageOpenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pages/open/$attId",
  component: () => <PagesGate><PageOpenPageLazy /></PagesGate>,
});
const wordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pages/word/$attId",
  component: () => <PagesGate><WordPageLazy /></PagesGate>,
});
// Таблицы Excel — Univer (xlsx как есть, формулы, сохранение в исходный файл).
const sheetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pages/sheet/$attId",
  component: () => <PagesGate><SheetPageLazy /></PagesGate>,
});

const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile",
  component: ProfilePage,
});

const productionRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/production",
  component: ProductionPage,
});

const productionDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/production/$projectId",
  component: ProductionDetailPage,
});

const visitsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/visits",
  component: VisitsPage,
});

const calculatorRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/calculator",
  component: CalculatorPage,
});

const specRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/spec",
  component: SpecPage,
});

const calculatorAdminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/calculator/admin",
  component: CalculatorAdminPage,
});

const profileViewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile/$userId",
  component: ProfilePage,
});


const adminAIDialogsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/ai-dialogs",
  component: AdminAIDialogsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRoute,
  messengerRoute,
  shareRoute,
  pagesRoute,
  pageEditorRoute,
  pageOpenRoute,
  wordRoute,
  sheetRoute,
  protectedRoute.addChildren([
    settingsRoute,
    indexRoute,
    healthRoute,
    managersRoute,
    callcenterRoute,
    violationsRoute,
    reportsRoute,
    callsRoute,
    chatRoute,
    profileRoute,
    profileViewRoute,
    productionRoute,
    productionDetailRoute,
    visitsRoute,
    calculatorRoute,
    calculatorAdminRoute,
    specRoute,
    adminAIDialogsRoute,
  ]),
]);


export function buildRouter(queryClient: QueryClient) {
  // На prod (path-prefix /v2/) выставляется VITE_BASE_PATH=/v2/.
  // basepath без trailing slash — иначе TanStack Router путается.
  const rawBase = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? "/";
  const basepath = rawBase === "/" ? undefined : rawBase.replace(/\/$/, "");
  // BrixChat (brixchat.ru, 04.10.2026): мессенджер живёт в корне. В адресной строке «/»,
  // внутри роутера — прежний /messenger, так что маршруты и navigate() не меняются.
  const brixRoot = typeof window !== "undefined" && /(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname);
  const swap = (url: URL, from: string, to: string) => {
    if (url.pathname !== from) return undefined;
    const u = new URL(url.href);
    u.pathname = to;
    return u;
  };
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    basepath,
    ...(brixRoot ? { rewrite: {
      input: ({ url }: { url: URL }) => swap(url, "/", "/messenger"),
      output: ({ url }: { url: URL }) => swap(url, "/messenger", "/"),
    } } : {}),
  });
}


declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
